import React, { useState, useEffect } from 'react';
import { Network, RefreshCw, Plus, Layers, Building2, Briefcase } from 'lucide-react';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import useDepartments from './hooks/useDepartments';
import usePositions from './hooks/usePositions';
import useOrgUnits from './hooks/useOrgUnits';
import {
  DepartmentForm,
  DepartmentList,
  PositionForm,
  PositionList,
  OrgChart,
  OrgUnitForm,
  OrgUnitList
} from './components';

export default function OrganigramaModule({ view = 'chart' }) {
  const { can } = usePermissionsContext();
  const canEdit = can('EMPLOYEE_EDIT');

  const {
    departments,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    refresh: refreshDepartments
  } = useDepartments();

  const {
    positions,
    addPosition,
    updatePosition,
    deletePosition,
    refresh: refreshPositions
  } = usePositions();

  const {
    orgUnits,
    addOrgUnit,
    updateOrgUnit,
    deleteOrgUnit,
    refresh: refreshOrgUnits
  } = useOrgUnits();

  const [activeTab, setActiveTab] = useState(view);

  const [showDepartmentForm, setShowDepartmentForm] = useState(false);
  const [showPositionForm, setShowPositionForm]     = useState(false);
  const [showOrgUnitForm, setShowOrgUnitForm]       = useState(false);
  const [editingDepartment, setEditingDepartment]   = useState(null);
  const [editingPosition, setEditingPosition]       = useState(null);
  const [editingOrgUnit, setEditingOrgUnit]         = useState(null);
  const [isRefreshing, setIsRefreshing]             = useState(false);

  useEffect(() => { handleManualRefresh(); }, []); // eslint-disable-line

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refreshDepartments?.(), refreshPositions?.(), refreshOrgUnits?.()]);
    } catch (error) {
      console.error('Error al refrescar:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Department handlers ──────────────────────────────────────────────
  const handleNewDepartment    = () => { setEditingDepartment(null); setShowDepartmentForm(true); };
  const handleEditDepartment   = (dept) => { setEditingDepartment(dept); setShowDepartmentForm(true); };
  const handleCancelDeptForm   = () => { setShowDepartmentForm(false); setEditingDepartment(null); };

  const handleSaveDepartment = async (deptData) => {
    try {
      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, deptData);
        alert('Departamento actualizado exitosamente');
      } else {
        await addDepartment(deptData);
        alert('Departamento creado exitosamente');
      }
      setShowDepartmentForm(false);
      setEditingDepartment(null);
      await handleManualRefresh();
    } catch (error) {
      console.error('Error al guardar departamento:', error);
      alert('Error al guardar el departamento. Por favor intente nuevamente.');
    }
  };

  const handleDeleteDepartment = async (id) => {
    const positionsInDept = positions.filter(p => p.departamentoId === id);
    if (positionsInDept.length > 0) {
      alert(`No se puede eliminar el departamento\n\nTiene ${positionsInDept.length} cargo(s) asociado(s).\nElimina primero esos cargos.`);
      return;
    }
    if (!window.confirm('¿Está seguro de eliminar este departamento?')) return;
    try {
      await deleteDepartment(id);
      alert('Departamento eliminado exitosamente');
      await handleManualRefresh();
    } catch (error) {
      console.error('Error eliminando departamento:', error);
    }
  };

  // ── OrgUnit handlers ─────────────────────────────────────────────────
  const handleNewOrgUnit    = () => { setEditingOrgUnit(null); setShowOrgUnitForm(true); };
  const handleEditOrgUnit   = (unit) => { setEditingOrgUnit(unit); setShowOrgUnitForm(true); };
  const handleCancelOUForm  = () => { setShowOrgUnitForm(false); setEditingOrgUnit(null); };

  const handleSaveOrgUnit = async (data) => {
    try {
      if (editingOrgUnit) {
        await updateOrgUnit(editingOrgUnit.id, data);
        alert('Unidad actualizada exitosamente');
      } else {
        await addOrgUnit(data);
        alert('Unidad creada exitosamente');
      }
      setShowOrgUnitForm(false);
      setEditingOrgUnit(null);
      await handleManualRefresh();
    } catch (error) {
      console.error('Error al guardar unidad:', error);
      const msg = error.response?.data?.error || 'Error al guardar la unidad. Por favor intente nuevamente.';
      alert(msg);
    }
  };

  const handleDeleteOrgUnit = async (id) => {
    try {
      await deleteOrgUnit(id);
      await handleManualRefresh();
    } catch (error) {
      console.error('Error eliminando unidad:', error);
    }
  };

  // ── Position handlers ────────────────────────────────────────────────
  const handleNewPosition = () => {
    if (departments.length === 0) {
      alert('Primero debe crear al menos un departamento');
      return;
    }
    setEditingPosition(null);
    setShowPositionForm(true);
  };
  const handleEditPosition   = (position) => { setEditingPosition(position); setShowPositionForm(true); };
  const handleCancelPosForm  = () => { setShowPositionForm(false); setEditingPosition(null); };

  const handleSavePosition = async (positionData) => {
    try {
      if (editingPosition) {
        await updatePosition(editingPosition.id, positionData);
        alert('Cargo actualizado exitosamente');
      } else {
        await addPosition(positionData);
        alert('Cargo creado exitosamente');
      }
      setShowPositionForm(false);
      setEditingPosition(null);
      await handleManualRefresh();
    } catch (error) {
      console.error('Error al guardar cargo:', error);
      alert('Error al guardar el cargo. Por favor intente nuevamente.');
    }
  };

  const handleDeletePosition = async (id) => {
    try {
      await deletePosition(id);
      alert('Cargo eliminado exitosamente');
      await handleManualRefresh();
    } catch (error) {
      console.error('Error eliminando cargo:', error);
    }
  };

  const TABS = [
    { id: 'chart',       label: 'Jerarquía',    icon: Network   },
    ...(canEdit ? [
      { id: 'units',       label: 'Unidades',     icon: Layers    },
      { id: 'departments', label: 'Departamentos', icon: Building2 },
      { id: 'positions',   label: 'Cargos',       icon: Briefcase },
    ] : []),
  ];

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
            <span className="font-semibold text-gray-700">{departments.length}</span> depts. ·{' '}
            <span className="font-semibold text-gray-700">{positions.length}</span> cargos
          </span>
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
            title="Actualizar datos"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {canEdit && (
          <div>
            {activeTab === 'units' && (
              <button
                onClick={handleNewOrgUnit}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
                Nueva Unidad
              </button>
            )}
            {activeTab === 'departments' && (
              <button
                onClick={handleNewDepartment}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
                Nuevo Departamento
              </button>
            )}
            {activeTab === 'positions' && (
              <button
                onClick={handleNewPosition}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
                Nuevo Cargo
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Content ──────────────────────────────────────────────────── */}

      {/* Unidades Organizativas */}
      {activeTab === 'units' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {showOrgUnitForm && (
            <div className="p-6 border-b border-gray-200">
              <OrgUnitForm
                unit={editingOrgUnit}
                orgUnits={orgUnits}
                onSave={handleSaveOrgUnit}
                onCancel={handleCancelOUForm}
              />
            </div>
          )}
          <OrgUnitList
            orgUnits={orgUnits}
            onEdit={handleEditOrgUnit}
            onDelete={handleDeleteOrgUnit}
          />
        </div>
      )}

      {/* Departamentos */}
      {activeTab === 'departments' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {showDepartmentForm && (
            <div className="p-6 border-b border-gray-200">
              <DepartmentForm
                department={editingDepartment}
                orgUnits={orgUnits}
                onSave={handleSaveDepartment}
                onCancel={handleCancelDeptForm}
              />
            </div>
          )}
          <DepartmentList
            departments={departments}
            positions={positions}
            orgUnits={orgUnits}
            onEdit={handleEditDepartment}
            onDelete={handleDeleteDepartment}
          />
        </div>
      )}

      {/* Cargos */}
      {activeTab === 'positions' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {showPositionForm && (
            <div className="p-6 border-b border-gray-200">
              <PositionForm
                position={editingPosition}
                departments={departments}
                positions={positions}
                onSave={handleSavePosition}
                onCancel={handleCancelPosForm}
              />
            </div>
          )}
          <PositionList
            positions={positions}
            departments={departments}
            onEdit={handleEditPosition}
            onDelete={handleDeletePosition}
          />
        </div>
      )}

      {/* Organigrama Visual — OrgChart manages its own card */}
      {activeTab === 'chart' && (
        departments.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 py-16 text-center">
            <Network size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 mb-1">No hay estructura definida</p>
            <p className="text-xs text-gray-400">Crea departamentos y cargos para visualizar el organigrama</p>
          </div>
        ) : (
          <OrgChart
            departments={departments}
            positions={positions}
            orgUnits={orgUnits}
            onPositionClick={handleEditPosition}
          />
        )
      )}
    </div>
  );
}
